import { PhoneIcon } from "@heroicons/react/24/outline";
import React from "react";
import { ParsedPhoneNumber, parsePhone } from "util/phone";
import { phoneUri } from "util/uri";
import {
    ContactValueLabel,
    ContactValueRow,
    contactValueLinkClasses,
    contactValueTextClasses,
    stopClickPropagation,
} from "./display-row";

export const phoneNumberParses = (value: string): boolean =>
    parsePhone(value).digits.startsWith("+");

export const telHref = (parsed: ParsedPhoneNumber): string => phoneUri(parsed.digits);

export const smsHref = (parsed: ParsedPhoneNumber): string => `sms:${parsed.digits}`;

export type PhoneActionIntent = "chooser" | "call" | "text";

export const phoneHrefForIntent = (
    parsed: ParsedPhoneNumber,
    intent: PhoneActionIntent,
): string => (intent === "text" ? smsHref(parsed) : telHref(parsed));

export interface PhoneTriggerBinding {
    href: string;
    title: string;
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface PhoneTriggerLinkProps {
    binding: PhoneTriggerBinding;
    display: string;
}

export const PhoneTriggerLink: React.FC<PhoneTriggerLinkProps> = props => {
    const { binding, display } = props;

    return (
        <a
            href={binding.href}
            title={binding.title}
            onClick={binding.onClick}
            className={contactValueLinkClasses}>
            {display}
        </a>
    );
};

interface UnparseablePhoneRowProps {
    value: string;
    label: string | null;
}

export const UnparseablePhoneRow: React.FC<UnparseablePhoneRowProps> = props => {
    const { value, label } = props;

    return (
        <ContactValueRow Icon={PhoneIcon}>
            <span className={contactValueTextClasses}>{value}</span>
            <ContactValueLabel label={label} />
        </ContactValueRow>
    );
};

interface DevicePhoneActionsProps {
    value: string;
    intent: PhoneActionIntent;
    renderTrigger: (binding: PhoneTriggerBinding) => React.ReactNode;
}

export const DevicePhoneActions: React.FC<DevicePhoneActionsProps> = props => {
    const { value, intent, renderTrigger } = props;

    const parsed = parsePhone(value);
    const href = phoneHrefForIntent(parsed, intent);
    const title = intent === "text" ? `Text ${parsed.display}` : `Call ${parsed.display}`;

    return <>{renderTrigger({ href, title, onClick: stopClickPropagation })}</>;
};
